import React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
    depascalize,
    Form,
    FormActions,
    FormDropdown,
    FormFields,
    FormMultiselectDropdown,
    Label,
    mapToDropdownOption,
    Optional
} from "sprint-common-web";

import { EncodeSpecificationContentType } from "../../../../../common/types/EncodeSpecificationContentType";
import { EditSpecificationFormInputs } from "./EditSpecificationFormInputs";
import { useEditSpecificationForm } from "./useEditSpecificationForm";

export interface EditSpecificationFormProps {
    onCancel(): void;
    onSubmit(): void;
    encodeSpecificationContentType: EncodeSpecificationContentType;
}

export const EditSpecificationForm: React.VFC<EditSpecificationFormProps> = ({
    encodeSpecificationContentType,
    onSubmit,
    onCancel
}: EditSpecificationFormProps) => {
    const { systemId } = useParams<{ systemId: string }>();

    const { itemToEdit, encodeSpecificationOptions, editSpecification } = useEditSpecificationForm(
        systemId,
        encodeSpecificationContentType
    );

    const { errors, control, formState, handleSubmit, watch, setValue } = useForm<EditSpecificationFormInputs>({
        defaultValues: {
            encodeSpecificationContentType: itemToEdit.encodeSpecificationContentType.toString(),
            defaultEncodeSpecificationId: itemToEdit.defaultEncodeSpecificationId,
            encodeSpecificationIds: itemToEdit.encodeSpecifications.map((encodeSpec) => encodeSpec.id)
        },
        criteriaMode: "all"
    });

    const handleEditSpecification = async (inputs: EditSpecificationFormInputs) => {
        await editSpecification(inputs);
        onSubmit();
    };

    const handleCancelButtonClick = () => {
        onCancel();
    };

    const handleDefaultEncodeSpecificationChange = (defaultEncodeSpecificationId: Optional<string>) => {
        if (!defaultEncodeSpecificationId) {
            setValue("encodeSpecificationIds", []);
        }

        setValue("encodeSpecificationIds", [
            ...watch("encodeSpecificationIds").filter((id) => id !== defaultEncodeSpecificationId)
        ]);
    };

    const defaultEncodeSpecificationOptions = encodeSpecificationOptions.map((item) => mapToDropdownOption(item));
    const additionalEncodeSpecificationOptions = encodeSpecificationOptions
        .filter((item) => item.id !== watch("defaultEncodeSpecificationId"))
        .map((item) => mapToDropdownOption(item));

    return (
        <Form onSubmit={handleSubmit(handleEditSpecification)}>
            <FormFields>
                <Label>{depascalize(EncodeSpecificationContentType[itemToEdit.encodeSpecificationContentType])}</Label>
                <FormDropdown
                    options={defaultEncodeSpecificationOptions}
                    control={control}
                    errors={errors}
                    rules={{ required: "Select default specification" }}
                    name="defaultEncodeSpecificationId"
                    label="Default specification"
                    onChange={handleDefaultEncodeSpecificationChange}
                />
                <FormMultiselectDropdown
                    options={additionalEncodeSpecificationOptions}
                    label="Additional specifications"
                    name="encodeSpecificationIds"
                    control={control}
                    disabled={!watch("defaultEncodeSpecificationId")}
                    errors={errors}
                />
            </FormFields>
            <FormActions
                isLoading={formState.isValidating || formState.isSubmitting}
                onCancel={handleCancelButtonClick}
            />
        </Form>
    );
};
